import { useCallback, useEffect } from 'react';

import { appConfig } from '../../../appConfig';
import { PriceType } from '../models/priceData/priceData';
import persistance from '../persistance/persistance';
import { featureFlagsSelectors } from '../selectors/featureFlagsSelectors';
import { sharedUiActions } from '../store/slices/ui/sharedUiSlice';
import { useIsB2BRoute } from '../utils/b2bUtils';
import { useProfileMutations, useProfileQuery } from './useProfile';
import { useAppDispatch, useAppSelector } from './useThunkDispatch';
import { useIsUserAuthenticated, useShoppingUserId } from './useUserProfile';

const getPriceTypeForContext = (
    preferredPriceType: PriceType | undefined,
    isB2BRoute: boolean,
    isPunchout: boolean,
) => {
    if (appConfig.coopSettings.featureFlags.enableB2B && !isB2BRoute) {
        return PriceType.IncludeTax;
    }

    // Punchout users cant set preferred price type in a toggle
    if (isPunchout) {
        return PriceType.ExcludeTax;
    }

    if (preferredPriceType) {
        return preferredPriceType;
    }

    return PriceType.IncludeTax;
};

const isPriceType = (priceType: string): priceType is PriceType => {
    return priceType === PriceType.IncludeTax || priceType === PriceType.ExcludeTax;
};

const useSavePriceType = () => {
    const shoppingUserId = useShoppingUserId() ?? 'anonymous';
    const dispatch = useAppDispatch();

    const savePriceType = useCallback(
        (priceType: 'IncludeTax' | 'ExcludeTax') => {
            if (isPriceType(priceType) && shoppingUserId) {
                switch (priceType) {
                    case PriceType.IncludeTax:
                        persistance.priceTypeStorage.setPricesWithTax(shoppingUserId);
                        break;
                    case PriceType.ExcludeTax:
                        persistance.priceTypeStorage.setPricesWithoutTax(shoppingUserId);
                        break;
                    default:
                        break;
                }

                dispatch(
                    sharedUiActions.setPriceType({
                        preferredPriceType: priceType,
                    }),
                );
            }
        },
        [dispatch, shoppingUserId],
    );

    return savePriceType;
};

export const useCurrentPriceType = () => {
    const preferredPriceType = useAppSelector((state) => state.ui.shared.preferredPriceType);
    const isPunchout = useAppSelector((state) => state.user.punchout?.isPunchout);
    const isB2BRoute = useIsB2BRoute();

    const isUserAuthenticated = useIsUserAuthenticated();
    const isUserProfileEnabled = useAppSelector((state) =>
        featureFlagsSelectors.selectDynamicFlagEnabled(state, 'EnableUserProfile'),
    );

    const { setPriceTypeMutation } = useProfileMutations();

    const { data: profilePriceType, isSuccess } = useProfileQuery();
    const savePriceType = useSavePriceType();

    useEffect(() => {
        if (!isUserProfileEnabled) return;

        const priceType = profilePriceType?.priceType;

        if (priceType && preferredPriceType === priceType) {
            return;
        }

        if (isSuccess && priceType) {
            // sync with profile if flag enabled
            savePriceType(priceType);
        }
    }, [preferredPriceType, isSuccess, isUserProfileEnabled, savePriceType, profilePriceType]);

    const currentPriceType = getPriceTypeForContext(preferredPriceType, isB2BRoute, isPunchout);

    const setPricesWithoutTax = useCallback(() => {
        if (isUserAuthenticated && isUserProfileEnabled) {
            setPriceTypeMutation.mutate(PriceType.ExcludeTax, {
                onSuccess: (profile) => {
                    if (profile.priceType) {
                        savePriceType(profile.priceType);
                    }
                },
            });
        } else {
            savePriceType(PriceType.ExcludeTax);
        }
    }, [savePriceType, isUserAuthenticated, setPriceTypeMutation, isUserProfileEnabled]);

    const setPricesWithTax = useCallback(() => {
        if (isUserAuthenticated && isUserProfileEnabled) {
            setPriceTypeMutation.mutate(PriceType.IncludeTax, {
                onSuccess: (profile) => {
                    if (profile.priceType) {
                        savePriceType(profile.priceType);
                    }
                },
            });
        } else {
            savePriceType(PriceType.IncludeTax);
        }
    }, [savePriceType, setPriceTypeMutation, isUserAuthenticated, isUserProfileEnabled]);

    return {
        currentPriceType,
        setPricesWithTax,
        setPricesWithoutTax,
    };
};
